/**
 * Copyright Protection Guide
 * Educational resource + monetized copyright submission assistance
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Layout } from '../components/Layout';
import { SeoHead } from '../components/SeoHead';
import { SEO } from '../config/seo';
import { 
  Shield, Check, X, AlertTriangle, ExternalLink, 
  FileText, Lock, Clock, DollarSign
} from 'lucide-react';
import { ServiceRequestModal } from '../components/ServiceRequestModal';

export function CopyrightProtection() {
  const { user } = useAuth();
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  const mustDoSteps = [
    {
      title: "U.S. Copyright Office Registration",
      cost: "$45-$65 per work",
      icon: <Shield className="w-6 h-6 text-green-600" />,
      link: "https://www.copyright.gov",
      linkText: "copyright.gov",
      strength: "Almost bulletproof in court",
      timeframe: "Protection instant, certificate in 3-8 months",
      description: "Register your final manuscript, screenplay, pilot script, or series bible as one unpublished collection.",
      steps: [
        "Go to copyright.gov",
        "Click Register a Literary Work or Motion Picture",
        "Upload your final PDF",
        "Pay $45-$65",
        "Get instant protection from upload date"
      ],
      priority: 1
    },
    {
      title: "WGA Registry (Writer's Guild)",
      cost: "$20 non-members / $10 members (valid 5 years)",
      icon: <FileText className="w-6 h-6 text-blue-600" />,
      link: "https://www.wgawregistry.org",
      linkText: "wgawregistry.org",
      strength: "Industry-standard date-proof",
      timeframe: "Instant registration",
      description: "Hollywood's accepted standard for proving creation date. Not as legally strong as copyright, but widely recognized.",
      steps: [
        "Visit wgawregistry.org or wgae.org/registry",
        "Upload pilot, feature script, or bible",
        "Pay $20 ($10 if WGA member)",
        "Valid for 5 years",
        "Renew before expiration"
      ],
      priority: 1
    }
  ];

  const recommendedSteps = [
    {
      title: "Timestamped PDF + Cloud Backup",
      cost: "Free",
      icon: <Clock className="w-6 h-6 text-purple-600" />,
      description: "Create version-controlled backups with clear dates.",
      steps: [
        "Name files: TITLE_Final_Draft_2025-11-30.pdf",
        "Store in Google Drive/Dropbox (enable version history)",
        "Keep copies on two external drives",
        "Email PDF to yourself (creates dated receipt)",
        "Never delete old versions"
      ]
    },
    {
      title: "Password-Protected PDFs",
      cost: "Free",
      icon: <Lock className="w-6 h-6 text-orange-600" />,
      description: "Never send unprotected Word docs or unlocked Final Draft files.",
      steps: [
        "Export as PDF (not editable Word/FDX)",
        "Add password: File → Export → PDF → Security",
        "Watermark every page: Confidential - Your Name 2025",
        "Add: Do Not Distribute Without Permission",
        "Use different passwords for different recipients"
      ]
    }
  ];

  const avoidThese = [
    {
      title: "Poor Man's Copyright",
      reason: "Widely considered worthless in court",
      description: "Mailing yourself a sealed copy has no legal weight anymore. Do not waste time or stamps."
    },
    {
      title: "Blockchain/NFT Services",
      reason: "Almost never used in actual lawsuits",
      description: "Services like Veritas, ScriptHop, FilmChain sound cool but are rarely accepted as evidence. Save your money."
    },
    {
      title: "Library of Congress Registration",
      reason: "Outdated (pre-1978)",
      description: "No longer needed - U.S. Copyright Office replaced it decades ago."
    }
  ];

  const handleServiceRequest = () => {
    if (!user) {
      alert('Please sign in to request copyright registration service');
      return;
    }
    // Create a placeholder document object for the modal
    setSelectedDocument({
      id: 'new',
      title: 'Your Document',
      doc_type: 'Document',
      word_count: 0
    });
    setShowServiceModal(true);
  };

  return (
    <Layout>
      <SeoHead {...SEO.copyright} />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 border-2 border-red-300 rounded-full mb-4">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <span className="font-semibold text-red-900">Work Theft is Real in the Industry</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
            Protect Your Work From Thieves
          </h1>
          
          <p className="text-xl text-gray-700 mb-6">
            Hollywood plagiarism cases happen every year. Protect your manuscripts and screenplays 
            with these industry-proven methods that have held up in court for 30+ years.
          </p>

          <Alert className="bg-green-50 border-green-300">
            <Check className="w-5 h-5 text-green-600" />
            <AlertDescription className="text-left">
              <strong className="text-green-900">Bottom Line:</strong> Do copyright.gov ($45) + WGA registry ($20) 
              and you are 99.9% protected. Everything you own can be fully secured for under $300 total. 
              <strong> Do it this week.</strong>
            </AlertDescription>
          </Alert>
        </div>

        {/* Must-Do Protection */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-2 text-center">
            Must-Do Protection
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Do these today — 30 minutes total
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {mustDoSteps.map((step, idx) => (
              <Card key={idx} className="border-2 border-green-300 shadow-lg">
                <CardHeader>
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-green-50 rounded-lg">
                      {step.icon}
                    </div>
                    <div className="flex-1">
                      <CardTitle className="text-xl mb-2">{step.title}</CardTitle>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                        <DollarSign className="w-4 h-4" />
                        <span>{step.cost}</span>
                      </div>
                      <a 
                        href={step.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-semibold text-sm"
                      >
                        {step.linkText}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                  <CardDescription className="mt-3">
                    {step.description}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="flex items-start gap-2 mb-2">
                        <Shield className="w-4 h-4 text-blue-600 mt-0.5" />
                        <div>
                          <div className="font-semibold text-blue-900">Legal Strength</div>
                          <div className="text-sm text-blue-800">{step.strength}</div>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <Clock className="w-4 h-4 text-blue-600 mt-0.5" />
                        <div>
                          <div className="font-semibold text-blue-900">Timeframe</div>
                          <div className="text-sm text-blue-800">{step.timeframe}</div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-2">How to Register:</h4>
                      <ol className="space-y-2">
                        {step.steps.map((s, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <span className="flex-shrink-0 w-5 h-5 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-xs font-bold">
                              {i + 1}
                            </span>
                            <span className="text-gray-700">{s}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Assisted Registration Service (Monetization) */}
        <div className="mb-12">
          <Card className="bg-gradient-to-r from-purple-600 to-blue-600 text-white border-0 shadow-2xl">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-3xl font-bold mb-4">
                    🛡️ Assisted Copyright Registration Service
                  </h3>
                  <p className="text-purple-100 mb-6">
                    Let us handle the paperwork for you. We will prepare and submit your copyright 
                    registration to the U.S. Copyright Office on your behalf.
                  </p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      <span>Professional PDF preparation with watermarks</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      <span>Complete copyright.gov submission</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      <span>Email confirmation + tracking</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5" />
                      <span>Certificate copy when received (3-8 months)</span>
                    </div>
                  </div>

                  <div className="bg-white/10 backdrop-blur rounded-lg p-4 mb-6">
                    <div className="text-2xl font-bold mb-1">$99 per work</div>
                    <div className="text-sm text-purple-200">
                      Includes $65 government fee + our service
                    </div>
                  </div>

                  <Button
                    onClick={handleServiceRequest}
                    className="w-full bg-white text-purple-600 hover:bg-gray-100"
                    size="lg"
                  >
                    Register Your Work Now →
                  </Button>
                </div>

                <div className="bg-white/10 backdrop-blur rounded-xl p-6">
                  <h4 className="font-semibold text-lg mb-4">Why Use Our Service?</h4>
                  <ul className="space-y-3 text-purple-100 text-sm">
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Save time - we handle all forms and submissions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>No mistakes - experts review before submission</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Proper formatting - PDFs secured with watermarks</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Status updates - we track your registration progress</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>Peace of mind - done right the first time</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recommended Steps */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-2 text-center">
            Recommended Best Practices
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Free steps that add extra layers of protection
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {recommendedSteps.map((step, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <div className="flex items-center gap-3 mb-2">
                    {step.icon}
                    <CardTitle>{step.title}</CardTitle>
                  </div>
                  <div className="text-sm font-semibold text-green-600">{step.cost}</div>
                  <CardDescription>{step.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {step.steps.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                        <Check className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>{s}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* What to Avoid */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold mb-2 text-center">
            What NOT to Waste Money On
          </h2>
          <p className="text-center text-gray-600 mb-8">
            These methods are outdated or legally worthless
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {avoidThese.map((item, idx) => (
              <Card key={idx} className="border-red-200 bg-red-50">
                <CardHeader>
                  <div className="flex items-center gap-2 mb-2">
                    <X className="w-5 h-5 text-red-600" />
                    <CardTitle className="text-lg">{item.title}</CardTitle>
                  </div>
                  <div className="text-sm font-semibold text-red-700">{item.reason}</div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <Card className="max-w-3xl mx-auto border-2 border-purple-300">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">
                Your Work Deserves Protection
              </h3>
              <p className="text-gray-700 mb-6">
                You have spent months or years creating your manuscript or screenplay. 
                Do not let someone steal it because you skipped a $65 registration.
              </p>
              
              <div className="space-y-3 mb-6">
                <Button
                  onClick={handleServiceRequest}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  size="lg"
                >
                  Use Our Registration Service ($99) →
                </Button>
                
                <p className="text-sm text-gray-600">
                  Or do it yourself at{' '}
                  <a 
                    href="https://www.copyright.gov"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-semibold"
                  >
                    copyright.gov
                  </a>
                  {' '}($45-$65) +{' '}
                  <a 
                    href="https://www.wgawregistry.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-semibold"
                  >
                    wgawregistry.org
                  </a>
                  {' '}($20)
                </p>
              </div>

              {user && (
                <Link to="/dashboard">
                  <Button variant="outline" className="w-full">
                    Go to Dashboard →
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Service Request Modal */}
      {showServiceModal && user && (
        <ServiceRequestModal
          document={selectedDocument}
          serviceType="copyright"
          onClose={() => setShowServiceModal(false)}
          onSuccess={() => {
            alert('Copyright registration request submitted successfully! We will contact you within 24-48 hours.');
            setShowServiceModal(false);
          }}
        />
      )}
      </div>
    </Layout>
  );
}
